/**
 * Collaboration sync helpers (Feature #5, scene-sync V1).
 * Thin, typed wrappers around Excalidraw's audited reconciliation plus a small
 * throttle used to rate-limit outbound scene broadcasts.
 */

import { reconcileElements } from "@excalidraw/excalidraw";

import type { OrderedExcalidrawElement } from "@excalidraw/element/types";
import type { RemoteExcalidrawElement } from "@excalidraw/excalidraw/data/reconcile";
import type { AppState } from "@excalidraw/excalidraw/types";

/**
 * Merge incoming remote elements into the local scene by element version
 * (Excalidraw's reconciler; remote wins on higher version, deterministic
 * tiebreak). Newcomers with an empty local scene simply receive the remote set.
 */
export function mergeRemoteElements(
  local: readonly OrderedExcalidrawElement[],
  remote: readonly OrderedExcalidrawElement[],
  appState: AppState,
): OrderedExcalidrawElement[] {
  return reconcileElements(
    local,
    remote as unknown as readonly RemoteExcalidrawElement[],
    appState,
  );
}

/**
 * Trailing-edge throttle: invoke `fn` at most once per `ms`, always delivering
 * the latest arguments. Used so a flurry of Excalidraw onChange events produces
 * a bounded stream of broadcasts.
 */
export function throttle<A extends unknown[]>(
  fn: (...args: A) => void,
  ms: number,
): ((...args: A) => void) & { cancel: () => void } {
  let last = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pending: A | null = null;

  const invoke = (args: A) => {
    last = Date.now();
    fn(...args);
  };

  const throttled = (...args: A) => {
    const now = Date.now();
    const remaining = ms - (now - last);
    pending = args;
    if (remaining <= 0) {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      invoke(args);
      pending = null;
    } else if (!timer) {
      timer = setTimeout(() => {
        timer = null;
        if (pending) {
          invoke(pending);
          pending = null;
        }
      }, remaining);
    }
  };

  throttled.cancel = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    pending = null;
  };

  return throttled;
}
